import { AsyncPipe, DOCUMENT, NgFor, NgIf } from "@angular/common";
import { Component, EventEmitter, Inject, Input, OnInit, Output, Version } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import {
  BRANCHNAME,
  ENABLE_FACEBOOK,
  getLanguageConfiguration,
  GITSHASHORT,
  supportedUserInterfaceLanguages4LetterISOForCandidate,
  supportedUserInterfaceLanguages4LetterISOForUser,
} from "@ankaadia/ankaadia-shared";
import { APP_VERSION, translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { Organization } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { isNull } from "lodash";
import { ConfirmationService, PrimeIcons, PrimeTemplate } from "primeng-v17/api";
import { ConfirmDialogModule } from "primeng-v17/confirmdialog";
import { OverlayPanelModule } from "primeng-v17/overlaypanel";
import { TooltipModule } from "primeng-v17/tooltip";
import { BehaviorSubject, filter, map, switchMap } from "rxjs";
import { ActivityService } from "../../features/activity/activity.service";
import { ChatBadgeComponent } from "../../features/chat/chat-badge/chat-badge.component";
import { ChatConfigurationService } from "../../features/chat/chat-configuration.service";
import { ChatPreviewService } from "../../features/chat/chat-preview.service";
import { ChatWidgetDialogService } from "../../features/chat/chat-widget-dialog/chat-widget-dialog.service";
import { MessageSuccessComponent } from "../../features/message/message-success/message-success.component";
import { MessageService } from "../../features/message/message.service";
import { NewAnkaadiaAnnouncementComponent } from "../../features/new-ankaadia-announcement/new-ankaadia-announcement.component";
import { NewAnkaadiaAnnouncementService } from "../../features/new-ankaadia-announcement/new-ankaadia-announcement.service";
import { NotificationBadgeComponent } from "../../features/notifications/notification-badge/notification-badge.component";
import { OrganizationSwitchComponent } from "../../features/organization-switch/organization-switch.component";
import { OrganizationSwitchService } from "../../features/organization-switch/organization-switch.service";
import { TranslationProposalButtonComponent } from "../../features/translationProposal/translationProposalButton/translationProposalButton.component";
import { UserConsentService } from "../../features/user-consent/user-consent.service";
import { UserEnvironmentService } from "../../features/user-environment/user-environment.service";
import { CommunicationLanguageComponent } from "../../features/users/communication-language/communication-language.component";
import { MessagesReceptionComponent } from "../../features/users/messages-reception/messages-reception.component";
import { UsersService } from "../../features/users/users.service";
import { AppendToBodyDirective } from "../../shared/primeng/append-to-body/append-to-body.directive";
import { MessengerService } from "../../shared/services/messengerService.service";
import { MyAccountService } from "../../shared/services/myAccount.service";
import { PromptUpdateService } from "../../shared/services/promptUpdateService";
import { SettingsService } from "../../shared/services/settings.service";
import { TranslateDirective } from "../../shared/transloco/translate.directive";
import { CustomMenuItem } from "../menu/menu.model";
import { MenuService } from "../menu/menu.service";

type MyOrganization = Pick<
  Organization,
  | "id"
  | "name"
  | "code"
  | "creatorOrganizationId"
  | "creatorOrganizationName"
  | "isCreatorMasterOrganization"
  | "isLicensed"
>;

@Component({
  selector: "app-topbar",
  templateUrl: "./topbar.component.html",
  styleUrl: "./topbar.component.scss",
  imports: [
    TranslateDirective,
    RouterLink,
    MessageSuccessComponent,
    NgFor,
    NgIf,
    MessagesReceptionComponent,
    CommunicationLanguageComponent,
    TooltipModule,
    OrganizationSwitchComponent,
    NotificationBadgeComponent,
    TranslationProposalButtonComponent,
    OverlayPanelModule,
    AppendToBodyDirective,
    PrimeTemplate,
    ConfirmDialogModule,
    AsyncPipe,
    ChatBadgeComponent,
    NewAnkaadiaAnnouncementComponent,
  ],
})
export class TopbarComponent implements OnInit {
  protected sections: CustomMenuItem[] = [];
  protected newAnkaadiaAnnouncementVisible = false;

  readonly availableLanguages = getLanguageConfiguration(
    this.settings.isCandidate
      ? supportedUserInterfaceLanguages4LetterISOForCandidate()
      : supportedUserInterfaceLanguages4LetterISOForUser()
  );

  readonly selectedLanguage = this.transloco.langChanges$.pipe(
    map((lang) => this.availableLanguages.find((l) => l.code === lang))
  );

  private readonly myOtherOrganizationSub = new BehaviorSubject<string>(null);
  readonly myOtherOrganizations$ = this.myOtherOrganizationSub.pipe(
    switchMap((x) => this.organizationSwitchService.list(x))
  );

  readonly showNotificationCount$ = this.activityService.notificationCount.pipe(map((x) => !isNull(x)));
  readonly notificationCount$ = this.activityService.notificationCount;

  readonly IS_FACEBOOK_LOGIN_ENABLED = ENABLE_FACEBOOK;
  readonly isMasterOrganization = this.settings.isMasterOrganization;
  readonly isNewUiEnabled = this.settings.isNewUiEnabled;
  readonly supportEmail = this.settings.supportEmail;
  readonly facebookUserId = this.settings.facebookUserId;
  readonly supportPhone = this.settings.supportPhone;
  readonly environmentName = this.settings.environmentName;
  readonly showEnvironmentName = this.settings.showEnvironmentName;
  readonly userDisplayName =
    this.settings.userDisplayName + (this.settings.isCandidate ? ` (${this.settings.userDisplayId})` : "");

  readonly userEmail = this.settings.userEmail;
  readonly myOrganization: MyOrganization = {
    id: this.settings.organizationId,
    name: this.settings.organizationName,
    code: this.settings.organizationCode,
    creatorOrganizationId: this.settings.creatorOrganizationId,
    creatorOrganizationName: this.settings.creatorOrganizationName,
    isCreatorMasterOrganization: this.settings.isCreatorMasterOrganization,
    isLicensed: this.settings.isLicensed,
  };

  readonly isCandidate = this.settings.isCandidate;
  readonly switchableOrganizationCount = this.settings.switchableOrganizationCount;

  readonly version =
    this.appVersion.full + (BRANCHNAME.startsWith("production") ? "" : ` (${BRANCHNAME}/${GITSHASHORT})`);

  readonly acceptedTC = this.consentService.userGaveConsent$;

  readonly isChatEnabled$ = this.chatConfigurationService.watchIsChatEnabled();
  readonly unreadMessagesCount$ = this.chatPreviewService.getUnreadMessagesCount();

  protected topBarConfirmationKey = "topBarConfirmationKey";

  @Input()
  activeTopbarItem: any;

  @Input()
  topbarMenuActive: boolean;

  @Output()
  readonly menuClicked = new EventEmitter<Event>();

  @Output()
  readonly menuItemClicked = new EventEmitter<[Event, any]>();

  @Output()
  readonly feedbackOpened = new EventEmitter<Event>();

  @Output()
  readonly notificationsOpened = new EventEmitter<Event>();

  protected get helpUrl(): string {
    return this.menuService.helpUrl;
  }

  constructor(
    private readonly settings: SettingsService,
    @Inject(APP_VERSION) private readonly appVersion: Version,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly ankaadiaAnnouncementService: NewAnkaadiaAnnouncementService,
    private readonly transloco: TranslocoService,
    private readonly menuService: MenuService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly myAccountService: MyAccountService,
    private readonly consentService: UserConsentService,
    private readonly messengerService: MessengerService,
    private readonly userSvc: UsersService,
    private readonly organizationSwitchService: OrganizationSwitchService,
    private readonly activityService: ActivityService,
    protected readonly promptUpdateService: PromptUpdateService,
    private readonly router: Router,
    private readonly chatWidgetDialogService: ChatWidgetDialogService,
    private readonly chatPreviewService: ChatPreviewService,
    private readonly chatConfigurationService: ChatConfigurationService,
    private readonly userEnvironmentService: UserEnvironmentService
  ) {}

  ngOnInit(): void {
    this.newAnkaadiaAnnouncementVisible = this.ankaadiaAnnouncementService.shouldShowAnnouncement();
    if (!this.availableLanguages.some((x) => x.code === this.transloco.getActiveLang())) {
      this.switchLanguage(<string>this.transloco.config.fallbackLang);
    }

    this.settings.isAuthorized$
      .pipe(filter((x) => x))
      .subscribe(() => (this.sections = this.menuService.getSections()));
  }

  switchLanguage(language: string): void {
    this.transloco.setActiveLang(language);
    this.document.defaultView.location.reload();
  }

  loadOrganizations(search: string): void {
    this.myOtherOrganizationSub.next(search);
  }

  switchOrganization(organizationId: string): void {
    this.settings.switchOrganization(organizationId);
  }

  openWidget(): void {
    this.chatWidgetDialogService.toggle();
  }

  navigateToChat(): void {
    void this.router.navigate(["/app/chats/widget"]);
  }

  resetPassword(event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.target,
      message: translateSafely("password.confirmReset"),
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      accept: () => {
        this.myAccountService.resetPassword().subscribe(() => {
          this.messageService.add({ severity: "success", summary: translateSafely("password.resetSuccess") });
        });
      },
    });
  }

  openOnlineApplicationCredentialHandling(_event: Event): void {
    void this.router.navigate(["/app/settings/online-applications/credentials"]);
  }

  toggleMessagesReception(change: { value: boolean; target: EventTarget }): void {
    this.confirmationService.confirm({
      target: change.target,
      message: change.value
        ? translateSafely("messagesReception.confirmationYes")
        : translateSafely("messagesReception.confirmationNo"),
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      accept: () => {
        this.userSvc.toggleMessagesReception(this.settings.isCandidate).subscribe(() => {
          this.messageService.add({ severity: "success", summary: translateSafely("messagesReception.resetSuccess") });
        });
      },
      reject: () => this.userSvc.messagesReceptionChanged.next(),
    });
  }

  copyUserEmailToClipboard(): void {
    void navigator.clipboard.writeText(this.userEmail);
    this.messageService.add({ severity: "success", summary: translateSafely("email.clipboard") });
  }

  copyEmailToClipboard(): void {
    void navigator.clipboard.writeText(this.supportEmail);
    this.messageService.add({ severity: "success", summary: translateSafely("email.clipboard") });
  }

  copyPhoneToClipboard(): void {
    void navigator.clipboard.writeText(this.supportPhone);
    this.messageService.add({ severity: "success", summary: translateSafely("phone.clipboard") });
  }

  sendTestMessage(): void {
    this.messengerService
      .sendMessageToCurrentUser(
        "This is a test message for validation purposes. In production messages will only be sent if important events have occured"
      )
      .subscribe();
  }

  loginNativeWithFacebook(): void {
    this.myAccountService.loginWithFacebookNative();
  }

  connectAccountWithFacebook(): void {
    this.confirmationService.confirm({
      message: translateSafely("connectWithFacebook.message"),
      target: null,
      key: this.topBarConfirmationKey,
      header: translateSafely("connectWithFacebook.header"),
      icon: PrimeIcons.FACEBOOK,
      accept: () =>
        this.myAccountService.linkLoggedInAccountWithFacebook().subscribe((x) =>
          this.messageService.add({
            severity: x ? "success" : "error",
            summary: x
              ? translateSafely("connectWithFacebook.success")
              : translateSafely("connectWithFacebook.failure"),
          })
        ),
    });
  }

  deleteCandidateData(event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.target,
      message: translateSafely("candidateDeletion.confirmOne"),
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      accept: () => {
        event.stopPropagation();
        this.myAccountService.deleteCandidateData().subscribe(() => {
          this.messageService.add({ severity: "success", summary: translateSafely("candidateDeletion.succes") });
        });
      },
    });
  }

  disconnectAccountFromFacebook(): void {
    this.confirmationService.confirm({
      message: translateSafely("disconnectFromFacebook.message"),
      target: null,
      key: this.topBarConfirmationKey,
      header: translateSafely("disconnectFromFacebook.header"),
      icon: PrimeIcons.FACEBOOK,
      accept: () =>
        this.myAccountService.unLinkLoggedInAccountWithFacebook().subscribe((x) =>
          this.messageService.add({
            severity: x ? "success" : "error",
            summary: x
              ? translateSafely("disconnectFromFacebook.success")
              : translateSafely("disconnectFromFacebook.failure"),
          })
        ),
    });
  }

  openKpiPage(event: Event): void {
    event.stopPropagation();
    void this.router.navigate(["/kpi"]);
  }

  startSwitchToNewUi(event: MouseEvent): void {
    if (!this.settings.isCandidate) {
      this.newAnkaadiaAnnouncementVisible = true;
    } else {
      this.userEnvironmentService.switchToAlternativeEnvironment(this.topBarConfirmationKey, event);
    }
  }
}
